import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  getMarketplaceApps,
  createApp,
  updateApp,
  createInstallation,
  getInstallations,
  uninstallInstallation,
  retryInstallation,
  rolloutUpdate,
} from "../api/marketplaceApi";
import { getEdges } from "../api/edgeApi";

export default function Marketplace() {
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const [apps, setApps] = useState([]);
  const [edges, setEdges] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [installing, setInstalling] = useState(null);
  const [rolloutApp, setRolloutApp] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState("");
  const [addAppOpen, setAddAppOpen] = useState(false);
  const [addAppSaving, setAddAppSaving] = useState(false);
  const [editAppOpen, setEditAppOpen] = useState(false);
  const [editAppSaving, setEditAppSaving] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [editAppForm, setEditAppForm] = useState({
    name: "",
    description: "",
    docker_image: "",
    port: 3000,
    path_prefix: "",
    icon: "",
    required_metrics: "",
  });
  const [newAppForm, setNewAppForm] = useState({
    slug: "",
    name: "",
    description: "",
    docker_image: "registry.digifab.me/",
    port: 3000,
    path_prefix: "",
    icon: "📦",
    required_metrics: "",
  });

  useEffect(() => {
    if (!accessToken) return;

    async function fetch() {
      try {
        setLoading(true);
        const [appsRes, edgesRes, instRes] = await Promise.all([
          getMarketplaceApps(accessToken),
          getEdges(accessToken),
          getInstallations(accessToken),
        ]);
        setApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
        setEdges(Array.isArray(edgesRes) ? edgesRes : edgesRes?.items || edgesRes?.data || []);
        setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
      } catch (err) {
        setError(err.response?.data?.detail || "Veriler alınamadı");
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [accessToken]);

  // Devam eden veya (tenant için) başarısız kurulum varken 10 sn'de bir güncelle
  useEffect(() => {
    if (!accessToken) return;
    const hasActive = installations.some((i) =>
      ["pending", "running", "update_pending"].includes(i.status)
    );
    const hasFailedForTenant = me?.global_role !== "superadmin" && installations.some((i) => i.status === "failed");
    if (!hasActive && !hasFailedForTenant) return;
    const iv = setInterval(async () => {
      try {
        const instRes = await getInstallations(accessToken);
        setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
      } catch {
        /* sessizce devam */
      }
    }, 10000);
    return () => clearInterval(iv);
  }, [accessToken, installations, me?.global_role]);

  const handleInstall = async (app) => {
    if (!selectedEdge) {
      toast.error("Önce bir edge seçin");
      return;
    }
    try {
      setInstalling(app.slug);
      const edge = edges.find((e) => e.id === selectedEdge || e.edgeId === selectedEdge);
      const edgeId = edge?.id || edge?.edgeId || selectedEdge;
      await createInstallation(accessToken, {
        app_id: app.slug,
        edge_id: edgeId,
      });
      toast.success(`${app.name} kurulum sıraya alındı. Edge 1-2 dakika içinde yükleyecek.`);
      const instRes = await getInstallations(accessToken);
      setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kurulum başarısız");
    } finally {
      setInstalling(null);
    }
  };

  const getInstallStatus = (appSlug, edgeId) => {
    return installations.find(
      (i) => i.app_id === appSlug && (i.edge_id === edgeId || i.edge_db_id === edgeId)
    );
  };

  const handleUninstall = async (inst) => {
    if (!inst) return;
    try {
      await uninstallInstallation(accessToken, inst.id);
      toast.success("Kaldırma sıraya alındı. Edge 1-2 dakika içinde kaldıracak.");
      const instRes = await getInstallations(accessToken);
      setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaldırma başarısız");
    }
  };

  const handleRetry = async (inst) => {
    if (!inst) return;
    try {
      await retryInstallation(accessToken, inst.id);
      toast.success("Tekrar deneme sıraya alındı.");
      const instRes = await getInstallations(accessToken);
      setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Tekrar deneme başarısız");
    }
  };

  const handleRollout = async (app) => {
    try {
      setRolloutApp(app.slug);
      const r = await rolloutUpdate(accessToken, app.slug);
      toast.success(`${r.edges_queued || 0} edge'e güncelleme yayıldı. Edge'ler ~45 sn–2 dk içinde güncelleyecek.`);
      const [instRes, appsRes] = await Promise.all([getInstallations(accessToken), getMarketplaceApps(accessToken)]);
      setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
      setApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncelleme yayılamadı");
    } finally {
      setRolloutApp(null);
    }
  };

  const getCompletedCountForApp = (appSlug) =>
    installations.filter((i) => i.app_id === appSlug && i.status === "completed").length;

  /** Superadmin: tüm kurulumlar. Tenant admin: sadece başarısız (bildirim için). Başarılı olunca kaybolur. */
  const visibleInstallations = me?.global_role === "superadmin"
    ? installations
    : installations.filter((i) => i.status === "failed");

  const needsRollout = (app) =>
    getCompletedCountForApp(app.slug) > 0 &&
    (app.docker_image !== app.rollout_docker_image || !app.rollout_docker_image);

  const openEditApp = (app) => {
    setEditApp(app);
    setEditAppForm({
      name: app.name || "",
      description: app.description || "",
      docker_image: app.docker_image || "",
      port: app.port ?? 3000,
      path_prefix: app.path_prefix || app.slug || "",
      icon: app.icon || "",
      required_metrics: Array.isArray(app.required_metrics) ? app.required_metrics.join(", ") : (app.required_metrics || ""),
    });
    setEditAppOpen(true);
  };

  const handleUpdateApp = async () => {
    if (!editApp) return;
    if (!editAppForm.docker_image?.trim()) {
      toast.error("Docker image gerekli");
      return;
    }
    try {
      setEditAppSaving(true);
      const payload = {
        name: (editAppForm.name || "").trim() || editApp.slug,
        description: (editAppForm.description || "").trim() || null,
        docker_image: editAppForm.docker_image.trim(),
        port: parseInt(editAppForm.port, 10) || 3000,
        path_prefix: (editAppForm.path_prefix || "").trim() || editApp.slug,
        icon: (editAppForm.icon || "").trim() || null,
        required_metrics: editAppForm.required_metrics
          ? editAppForm.required_metrics.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
      };
      await updateApp(accessToken, editApp.slug, payload);
      toast.success(`"${editApp.name}" güncellendi. Güncellemeyi edge'lere yaymak için "Güncellemeyi Yay" butonunu kullanın.`);
      setEditAppOpen(false);
      setEditApp(null);
      const appsRes = await getMarketplaceApps(accessToken);
      setApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncellenemedi");
    } finally {
      setEditAppSaving(false);
    }
  };

  const handleAddApp = async () => {
    const slug = (newAppForm.slug || "").trim().toLowerCase().replace(/\s+/g, "-");
    if (!slug) {
      toast.error("Slug gerekli");
      return;
    }
    if (!newAppForm.docker_image?.trim()) {
      toast.error("Docker image gerekli (örn: registry.digifab.me/my-app:1.0)");
      return;
    }
    const path_prefix = (newAppForm.path_prefix || slug).trim() || slug;
    try {
      setAddAppSaving(true);
      const created = await createApp(accessToken, {
        slug,
        name: (newAppForm.name || "").trim() || slug,
        description: (newAppForm.description || "").trim() || null,
        docker_image: newAppForm.docker_image.trim(),
        port: parseInt(newAppForm.port, 10) || 3000,
        path_prefix,
        icon: (newAppForm.icon || "").trim() || null,
        required_metrics: newAppForm.required_metrics
          ? newAppForm.required_metrics.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
      });
      const img = created.docker_image || newAppForm.docker_image.trim();
      const tag = img.includes(":") ? img : `${img}:1.0`;
      toast.success(`"${created.name}" eklendi.`, {
        autoClose: 8000,
        bodyStyle: { fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", marginTop: 8 },
      });
      toast.info(`Komutlar:\ndocker build -t ${created.slug}:1.0 .\ndocker tag ${created.slug}:1.0 ${tag}\ndocker push ${tag}`, {
        autoClose: 12000,
        bodyStyle: { fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap" },
      });
      setAddAppOpen(false);
      setNewAppForm({ slug: "", name: "", description: "", docker_image: "registry.digifab.me/", port: 3000, path_prefix: "", icon: "📦", required_metrics: "" });
      const appsRes = await getMarketplaceApps(accessToken);
      setApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eklenemedi");
    } finally {
      setAddAppSaving(false);
    }
  };

  const STATUS_LABELS = { pending: "Bekliyor", running: "Kuruluyor", update_pending: "Güncelleniyor", completed: "Kurulu", failed: "Başarısız", uninstall_pending: "Kaldırılıyor", uninstalled: "Kaldırıldı" };

  const getChipColor = (status) => {
    if (status === "completed") return "#238636";
    if (status === "failed") return "#da3633";
    if (status === "update_pending" || status === "running") return "#f0883e";
    return "#8957e5";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" color="#e6edf3">
        <CircularProgress sx={{ color: "#4fc3f7" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, color: "#e6edf3" }}>
      <Typography variant="h5" gutterBottom sx={{ color: "#e6edf3", fontWeight: 600 }}>
        Marketplace
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "#8b949e" }}>
        Uygulamayı seçin, edge cihazını seçin ve yükleyin. Edge cihazı 1-2 dakika içinde kurulumu uygular.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {me?.global_role === "superadmin" && (
        <Button
          variant="outlined"
          onClick={() => setAddAppOpen(true)}
          sx={{ mb: 2, mr: 2, borderColor: "#8957e5", color: "#8957e5", "&:hover": { borderColor: "#9b6eef", backgroundColor: "rgba(137,87,229,0.1)" } }}
        >
          + Yeni Uygulama Ekle
        </Button>
      )}

      <FormControl
        size="small"
        sx={{
          minWidth: 240,
          mb: 3,
          "& .MuiOutlinedInput-root": { color: "#e6edf3" },
          "& .MuiInputLabel-root": { color: "#8b949e" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
          "& .MuiSvgIcon-root": { color: "#8b949e" },
        }}
      >
        <InputLabel>Edge Cihazı Seçin</InputLabel>
        <Select
          value={selectedEdge}
          label="Edge Cihazı Seçin"
          onChange={(e) => setSelectedEdge(e.target.value)}
          sx={{ color: "#e6edf3" }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#1a1a2e",
                color: "#e6edf3",
                "& .MuiMenuItem-root": { color: "#e6edf3" },
                "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(79,195,247,0.2)" },
              },
            },
          }}
        >
          <MenuItem value="">
            <em>Seçin</em>
          </MenuItem>
          {edges.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.edgeId || e.edge_id || e.id} {e.asset?.assetLocation && `- ${e.asset.assetLocation}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Kurulum süreci: superadmin tümünü görür; tenant_admin sadece başarısızları (bildirim için) */}
      {(() => {
        const visible = me?.global_role === "superadmin"
          ? installations
          : installations.filter((i) => i.status === "failed");
        if (visible.length === 0) return null;
        return (
        <Box sx={{ mb: 3, p: 2, backgroundColor: "#1a1a2e", borderRadius: 2, border: "1px solid rgba(255,255,255,0.1)" }}>
          <Typography variant="subtitle1" sx={{ mb: 2, color: "#e6edf3", fontWeight: 600 }}>
            {me?.global_role === "superadmin" ? "Kurulum Süreci" : "Başarısız Kurulumlar (destek için bildirin)"}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {[...visible]
              .sort((a, b) => {
                const order = { update_pending: 0, pending: 1, running: 2 };
                const ao = order[a.status] ?? 99, bo = order[b.status] ?? 99;
                if (ao !== bo) return ao - bo;
                return (new Date(b.created_at) || 0) - (new Date(a.created_at) || 0);
              })
              .slice(0, 20)
              .map((i) => (
              <Box
                key={i.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" sx={{ color: "#e6edf3" }}>
                  {i.edge_id || i.edge_db_id} / {i.app_id}
                </Typography>
                <Chip size="small" label={STATUS_LABELS[i.status] || i.status} sx={{ backgroundColor: getChipColor(i.status), color: "#fff" }} />
                {i.error && <Typography variant="caption" sx={{ color: "#8b949e", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }} title={i.error}>{i.error}</Typography>}
                {i.created_at && <Typography variant="caption" sx={{ color: "#8b949e" }}>{new Date(i.created_at).toLocaleString("tr-TR")}</Typography>}
              </Box>
            ))}
          </Box>
        </Box>
        );
      })()}

      <Grid container spacing={2}>
        {apps.map((app) => {
          const inst = selectedEdge
            ? getInstallStatus(app.slug, selectedEdge)
            : null;
          const isPending = inst && ["pending", "running"].includes(inst.status);

          return (
            <Grid item xs={12} sm={6} md={4} key={app.id}>
              <Card
                sx={{
                  backgroundColor: "#1a1a2e",
                  color: "#e6edf3",
                  border: "1px solid rgba(255,255,255,0.1)",
                  "& .MuiCardContent-root": { color: "#e6edf3" },
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#e6edf3", fontWeight: 600 }}>
                    {app.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: "#8b949e" }}>
                    {app.description || app.slug}
                  </Typography>
                  {me?.global_role === "superadmin" && (
                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditApp(app)}
                        sx={{ borderColor: "#58a6ff", color: "#58a6ff", "&:hover": { borderColor: "#79b8ff", backgroundColor: "rgba(88,166,255,0.1)" } }}
                      >
                        Düzenle
                      </Button>
                      {(app.needs_rollout || (getCompletedCountForApp(app.slug) > 0 && (app.docker_image !== app.rollout_docker_image || !app.rollout_docker_image))) && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={rolloutApp === app.slug}
                          onClick={() => handleRollout(app)}
                          sx={{ borderColor: "#8957e5", color: "#8957e5", "&:hover": { borderColor: "#9b6eef", backgroundColor: "rgba(137,87,229,0.1)" } }}
                        >
                          {rolloutApp === app.slug ? "Yayılıyor..." : `Güncellemeyi Yay (${getCompletedCountForApp(app.slug)} edge)`}
                        </Button>
                      )}
                    </Box>
                  )}
                  {app.required_metrics && app.required_metrics.length > 0 && (
                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {app.required_metrics.map((m) => (
                        <Chip
                          key={m}
                          label={m}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: "#8b949e",
                            borderColor: "rgba(255,255,255,0.2)",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {inst?.status === "completed" ? (
                    <>
                      <Chip size="small" label="Kurulu" sx={{ backgroundColor: "#238636", color: "#fff" }} />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleUninstall(inst)}
                        sx={{ borderColor: "#da3633", color: "#da3633", "&:hover": { borderColor: "#f85149", backgroundColor: "rgba(218,54,51,0.1)" } }}
                      >
                        Kaldır
                      </Button>
                    </>
                  ) : inst?.status === "failed" ? (
                    <>
                      <Chip size="small" label="Başarısız" sx={{ backgroundColor: "#da3633", color: "#fff" }} />
                      <Button size="small" variant="contained" onClick={() => handleRetry(inst)} sx={{ backgroundColor: "#8957e5", "&:hover": { backgroundColor: "#9b6eef" } }}>
                        Tekrar Dene
                      </Button>
                    </>
                  ) : isPending || ["uninstall_pending"].includes(inst?.status) ? (
                    <Chip size="small" label={STATUS_LABELS[inst?.status] || inst?.status} sx={{ backgroundColor: "#8957e5", color: "#fff" }} />
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!selectedEdge || installing === app.slug}
                      onClick={() => handleInstall(app)}
                      sx={{
                        backgroundColor: "#238636",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#2ea043" },
                        "&.Mui-disabled": { backgroundColor: "#333", color: "#666" },
                      }}
                    >
                      {installing === app.slug ? "Yükleniyor..." : "Bu Edge'e Yükle"}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {apps.length === 0 && !loading && (
        <Typography sx={{ mt: 3, color: "#8b949e" }}>
          Henüz marketplace uygulaması yok. OEE ve PdM eklemek için seed script çalıştırın.
        </Typography>
      )}

      <Dialog
        open={addAppOpen}
        onClose={() => !addAppSaving && setAddAppOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a2e",
            color: "#e6edf3",
            border: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#e6edf3" }}>Yeni Uygulama Ekle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 400, mt: 1 }}>
            <TextField
              label="Slug (benzersiz)"
              value={newAppForm.slug}
              onChange={(e) => setNewAppForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="ornek-uygulama"
              InputProps={{ sx: { color: "#e6edf3" } }}
              InputLabelProps={{ sx: { color: "#8b949e" } }}
            />
            <TextField
              label="Ad"
              value={newAppForm.name}
              onChange={(e) => setNewAppForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Örnek Uygulama"
              InputProps={{ sx: { color: "#e6edf3" } }}
              InputLabelProps={{ sx: { color: "#8b949e" } }}
            />
            <TextField
              label="Açıklama"
              value={newAppForm.description}
              onChange={(e) => setNewAppForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Opsiyonel açıklama"
              multiline
              InputProps={{ sx: { color: "#e6edf3" } }}
              InputLabelProps={{ sx: { color: "#8b949e" } }}
            />
            <TextField
              label="Docker image"
              value={newAppForm.docker_image}
              onChange={(e) => setNewAppForm((f) => ({ ...f, docker_image: e.target.value }))}
              placeholder="registry.digifab.me/my-app:1.0"
              InputProps={{ sx: { color: "#e6edf3" } }}
              InputLabelProps={{ sx: { color: "#8b949e" } }}
              helperText="İmajı build edip push edin"
            />
            <TextField
              label="Port"
              type="number"
              value={newAppForm.port}
              onChange={(e) => setNewAppForm((f) => ({ ...f, port: e.target.value }))}
              InputProps={{ sx: { color: "#e6edf3" } }}
              InputLabelProps={{ sx: { color: "#8b949e" } }}
            />
            <TextField
              label="Path prefix"
              value={newAppForm.path_prefix}
              onChange={(e) => setNewAppForm((f) => ({ ...f, path_prefix: e.target.value }))}
              placeholder="/my-app"
              InputProps={{ sx: { color: "#e6edf3" } }}
              InputLabelProps={{ sx: { color: "#8b949e" } }}
            />
            <TextField
              label="Icon (emoji)"
              value={newAppForm.icon}
              onChange={(e) => setNewAppForm((f) => ({ ...f, icon: e.target.value }))}
              placeholder="📦"
              InputProps={{ sx: { color: "#e6edf3" } }}
              InputLabelProps={{ sx: { color: "#8b949e" } }}
            />
            <TextField
              label="Required metrics (virgülle ayırın)"
              value={newAppForm.required_metrics}
              onChange={(e) => setNewAppForm((f) => ({ ...f, required_metrics: e.target.value }))}
              placeholder="temperature, vibration"
              InputProps={{ sx: { color: "#e6edf3" } }}
              InputLabelProps={{ sx: { color: "#8b949e" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          <Button
            onClick={() => setAddAppOpen(false)}
            disabled={addAppSaving}
            sx={{ color: "#8b949e" }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleAddApp}
            disabled={addAppSaving}
            sx={{ backgroundColor: "#8957e5", "&:hover": { backgroundColor: "#9b6eef" } }}
          >
            {addAppSaving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editAppOpen}
        onClose={() => !editAppSaving && (setEditAppOpen(false), setEditApp(null))}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a2e",
            color: "#e6edf3",
            border: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#e6edf3" }}>
          Uygulama Düzenle {editApp && `— ${editApp.name}`}
        </DialogTitle>
        <DialogContent>
          {editApp && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 400, mt: 1 }}>
              <TextField
                label="Ad"
                value={editAppForm.name}
                onChange={(e) => setEditAppForm((f) => ({ ...f, name: e.target.value }))}
                InputProps={{ sx: { color: "#e6edf3" } }}
                InputLabelProps={{ sx: { color: "#8b949e" } }}
              />
              <TextField
                label="Açıklama"
                value={editAppForm.description}
                onChange={(e) => setEditAppForm((f) => ({ ...f, description: e.target.value }))}
                multiline
                InputProps={{ sx: { color: "#e6edf3" } }}
                InputLabelProps={{ sx: { color: "#8b949e" } }}
              />
              <TextField
                label="Docker image"
                value={editAppForm.docker_image}
                onChange={(e) => setEditAppForm((f) => ({ ...f, docker_image: e.target.value }))}
                placeholder="registry.digifab.me/my-app:1.1"
                InputProps={{ sx: { color: "#e6edf3" } }}
                InputLabelProps={{ sx: { color: "#8b949e" } }}
                helperText="Yeni sürüm imajı (örn: 1.0 → 1.1). Güncelledikten sonra 'Güncellemeyi Yay' ile edge'lere yayın."
              />
              <TextField
                label="Port"
                type="number"
                value={editAppForm.port}
                onChange={(e) => setEditAppForm((f) => ({ ...f, port: e.target.value }))}
                InputProps={{ sx: { color: "#e6edf3" } }}
                InputLabelProps={{ sx: { color: "#8b949e" } }}
              />
              <TextField
                label="Path prefix"
                value={editAppForm.path_prefix}
                onChange={(e) => setEditAppForm((f) => ({ ...f, path_prefix: e.target.value }))}
                InputProps={{ sx: { color: "#e6edf3" } }}
                InputLabelProps={{ sx: { color: "#8b949e" } }}
              />
              <TextField
                label="Icon"
                value={editAppForm.icon}
                onChange={(e) => setEditAppForm((f) => ({ ...f, icon: e.target.value }))}
                InputProps={{ sx: { color: "#e6edf3" } }}
                InputLabelProps={{ sx: { color: "#8b949e" } }}
              />
              <TextField
                label="Required metrics (virgülle ayırın)"
                value={editAppForm.required_metrics}
                onChange={(e) => setEditAppForm((f) => ({ ...f, required_metrics: e.target.value }))}
                InputProps={{ sx: { color: "#e6edf3" } }}
                InputLabelProps={{ sx: { color: "#8b949e" } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          <Button
            onClick={() => { setEditAppOpen(false); setEditApp(null); }}
            disabled={editAppSaving}
            sx={{ color: "#8b949e" }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateApp}
            disabled={editAppSaving}
            sx={{ backgroundColor: "#8957e5", "&:hover": { backgroundColor: "#9b6eef" } }}
          >
            {editAppSaving ? "Kaydediliyor..." : "Güncelle"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
